import { CacheProvider } from '@emotion/react';
import type { EmotionCache } from '@emotion/react';
import Grid from '@mui/material/Grid';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useState } from 'react';

import { WAITLIST } from 'reservations/account/components/PageTitleHandler';
import { PageTitleContext } from 'reservations/account/providers/PageTitleProvider';
import Analytics from 'reservations/analytics/components/Analytics';
import { NodeEnv } from 'reservations/base/constants/environments';
import { useRouterNav } from 'reservations/base/hooks/useRouterNav';
import ReservationsProvider from 'reservations/base/providers/ReservationsProvider';
import { RouterProvider } from 'reservations/base/providers/RouterProvider';
import setupAxeCore from 'utility/axeCoreTool';
import createEmotionCache from 'utility/createEmotionCache';
import { initializeDatadog } from 'utility/datadog';
import { initializeHeap } from 'utility/heap';
import { initializeSentry } from 'utility/sentry';
import { initializeSmartlook } from 'utility/smartlook';

initializeHeap();
initializeSentry();
initializeSmartlook();
initializeDatadog();
setupAxeCore();

const clientSideEmotionCache = createEmotionCache();

interface CustomAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

// NOTE: No need for coverage for this branch
// istanbul ignore next
if (
  process.env.NEXT_PUBLIC_API_MOCKING === 'enabled' &&
  process.env.NODE_ENV !== NodeEnv.PRODUCTION
) {
  require('../mocks');
}

function ReservationsApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache
}: CustomAppProps) {
  const { handleNavigation, handleOffRouteChange, handleOnRouteChange } =
    useRouterNav();

  const [faviconUrl, setFaviconUrl] = useState('');
  const [feature, setFeature] = useState(WAITLIST);
  const [restaurant, setRestaurant] = useState('');

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>{`${feature} | ${restaurant}`}</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link
          rel="icon"
          href={faviconUrl}
          type="image/<generated>"
          sizes="<generated>"
        />
      </Head>
      <RouterProvider
        handleNavigation={handleNavigation}
        handleOffRouteChange={handleOffRouteChange}
        handleOnRouteChange={handleOnRouteChange}
      >
        <PageTitleContext.Provider
          value={{ setFaviconUrl, setFeature, setRestaurant }}
        >
          <ReservationsProvider>
            <Analytics />
            <Grid
              container
              direction="column"
              wrap="nowrap"
              sx={{ height: '100vh' }}
            >
              <Grid item xs={11}>
                <Component {...pageProps} />
              </Grid>
            </Grid>
          </ReservationsProvider>
        </PageTitleContext.Provider>
      </RouterProvider>
    </CacheProvider>
  );
}

export default ReservationsApp;
